import React from 'react'
import { Box } from '@chakra-ui/react'
import { motion, useReducedMotion } from 'framer-motion'
import images from 'theme/images'

/**
 * PIA has one face across the product.
 *
 * She turns up in registration as a guide, in the analytics builder as an
 * assistant, and on the report advisor — three features that previously drew
 * her three different ways (a hand-built SVG tree in one, a lightning glyph in
 * the others). This component is the single place her likeness lives, so a
 * change to the artwork reaches every surface at once.
 *
 * The illustration carries its own ring and backdrop, so nothing here adds a
 * border or a frame; it only sizes her and gives her a little life.
 */

export type PiaMood = 'idle' | 'thinking' | 'happy' | 'working'

export type PiaAvatarProps = {
  /** Rendered size in pixels, square. */
  size?: number
  mood?: PiaMood
  /** Override the accessible name where the surrounding copy already says who she is. */
  alt?: string
  /** Hide her from screen readers when a visible label right beside her already names her. */
  decorative?: boolean
}

const MotionBox = motion(Box)

/**
 * The small asset is 128px, so it stays crisp up to 64 CSS pixels on a 2x
 * screen and is six times lighter than the full one. Above that, the retina
 * pixels are not there and she goes soft, so the full asset is used.
 */
const SMALL_ASSET_MAX = 64

const MOOD_MOTION: Record<PiaMood, { animate: any; duration: number }> = {
  // A slow rise and fall, so she reads as present rather than pasted on.
  idle: { animate: { y: [0, -4, 0] }, duration: 3.6 },
  // A considering tilt while she works out an answer.
  thinking: { animate: { rotate: [0, -3, -3, 0], y: [0, -2, 0] }, duration: 3 },
  happy: { animate: { y: [0, -10, 0], rotate: [0, -2.5, 2.5, 0] }, duration: 1.05 },
  working: { animate: { rotate: [0, 1.4, 0], y: [0, -1.5, 0] }, duration: 2.4 }
}

const PiaAvatar: React.FC<PiaAvatarProps> = ({
  size = 40,
  mood = 'idle',
  alt = 'PIA, your Baotree assistant',
  decorative = false
}) => {
  const shouldReduceMotion = useReducedMotion()
  const src = size <= SMALL_ASSET_MAX ? images.piaAvatarSmall : images.piaAvatar
  const { animate, duration } = MOOD_MOTION[mood]

  return (
    <MotionBox
      as="img"
      src={src}
      alt={decorative ? '' : alt}
      aria-hidden={decorative || undefined}
      role={decorative ? 'presentation' : 'img'}
      draggable={false}
      w={`${size}px`}
      h={`${size}px`}
      flexShrink={0}
      display="block"
      userSelect="none"
      objectFit="cover"
      initial={false}
      animate={shouldReduceMotion ? undefined : animate}
      transition={
        shouldReduceMotion ? undefined : { duration, repeat: Infinity, ease: 'easeInOut' }
      }
    />
  )
}

PiaAvatar.displayName = 'PiaAvatar'

export default React.memo(PiaAvatar)
